import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  FiActivity,
  FiAlertTriangle,
  FiArrowUpRight,
  FiCheckCircle,
  FiClock,
  FiLayers,
  FiRefreshCw,
  FiTrendingDown,
  FiTrendingUp,
} from 'react-icons/fi';
import { getRetailReportByPath } from './reportConfigs';
import { getPortalScope, PORTAL_SCOPE_CHANGE_EVENT } from 'utils/portalScope';
import {
  getSalesOverviewReport,
  getRevenueByStoreReport,
  getSalesStaffProductivityReport,
} from 'services/retail-report.service';
import { buildRetailReportFromApi } from './retailReportData';
import SalesReportFilters, { getSalesReportQuery } from './SalesReportFilters';
import {
  ReportContextBar,
  ReportKpiGrid,
  ReportPageHeader,
  ReportPageLayout,
  ReportState,
} from './ReportComponents';
import './retailReports.scss';

const PANEL_ICON_MAP = {
  line: FiTrendingUp,
  bars: FiActivity,
  donut: FiLayers,
  table: FiArrowUpRight,
  leaderboard: FiTrendingUp,
  alerts: FiAlertTriangle,
  stacked: FiLayers,
  funnel: FiTrendingDown,
  heatmap: FiLayers,
  timeline: FiClock,
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const buildLinePoints = (values = [], width = 680, height = 220, padding = 20, bounds) => {
  if (!values.length) {
    return '';
  }

  const maxValue = bounds?.max ?? Math.max(...values);
  const minValue = bounds?.min ?? Math.min(...values);
  const range = maxValue - minValue || 1;
  const innerWidth = width - padding * 2;
  const innerHeight = height - padding * 2;

  return values
    .map((value, index) => {
      const x = padding + (innerWidth / Math.max(values.length - 1, 1)) * index;
      const y = padding + innerHeight - ((value - minValue) / range) * innerHeight;
      return `${x},${y}`;
    })
    .join(' ');
};

const buildAreaPath = (points = '', width = 680, height = 220, padding = 20) => {
  if (!points) {
    return '';
  }

  const coords = points.split(' ');
  const first = coords[0]?.split(',') || [padding, height - padding];
  const last = coords[coords.length - 1]?.split(',') || [width - padding, height - padding];

  return `M ${first[0]} ${height - padding} L ${coords.join(' L ')} L ${last[0]} ${height - padding} Z`;
};

const getDeltaToneClass = (tone = '') => {
  if (tone === 'positive') return 'is-positive';
  if (tone === 'danger') return 'is-danger';
  if (tone === 'warning') return 'is-warning';
  return 'is-neutral';
};

const LinePanel = ({ panel }) => (
  <div className='bc_rr_panel_body'>
    <svg className='bc_rr_chart_svg' viewBox='0 0 680 220' preserveAspectRatio='none'>
      {[0, 1, 2, 3].map((index) => (
        <line key={index} x1='20' x2='660' y1={40 + index * 45} y2={40 + index * 45} className='bc_rr_chart_grid' />
      ))}
      {panel.series.map((series) => {
        const allValues = panel.series.flatMap((item) => item.values).filter(Number.isFinite);
        const bounds = { min: Math.min(0, ...allValues), max: Math.max(1, ...allValues) };
        const points = buildLinePoints(series.values, 680, 220, 20, bounds);
        const areaPath = buildAreaPath(points);
        const pointList = points.split(' ');
        const paths = [];
        let current = [];
        series.values.forEach((value, index) => {
          if (Number.isFinite(value)) current.push(pointList[index]);
          else if (current.length) { paths.push(current); current = []; }
        });
        if (current.length) paths.push(current);
        return (
          <g key={series.label}>
            {series.area && series.values.every(Number.isFinite) ? <path d={areaPath} fill={`${series.color}16`} /> : null}
            {paths.map((part, index) => <polyline key={index} points={part.join(' ')} fill='none' stroke={series.color} strokeWidth='4' strokeDasharray={series.forecast ? '7 5' : undefined} strokeLinecap='round' strokeLinejoin='round' />)}
            {series.values.map((value, index) => Number.isFinite(value) ? <circle key={index} cx={pointList[index].split(',')[0]} cy={pointList[index].split(',')[1]} r='3' fill={series.color}><title>{`${panel.labels[index]} · ${series.label}: ${value.toLocaleString('vi-VN', { maximumFractionDigits: 2 })}${panel.unit || ''}`}</title></circle> : null)}
          </g>
        );
      })}
    </svg>
    <div className='bc_rr_chart_legend'>
      {panel.series.map((series) => (
        <div key={series.label}>
          <span className='bc_rr_chart_legend_dot' style={{ backgroundColor: series.color }} />
          <strong>{series.label}</strong>
        </div>
      ))}
    </div>
    <div className='bc_rr_axis_labels'>
      {panel.labels.map((label) => (
        <span key={label}>{label}</span>
      ))}
    </div>
    {panel.footer ? <p className='bc_rr_panel_footer'>{panel.footer}</p> : null}
  </div>
);

const BarsPanel = ({ panel }) => {
  const maxValue = Math.max(...panel.bars.map((bar) => bar.value)) || 1;

  return (
    <div className='bc_rr_panel_body'>
      {panel.bars.length ? <div className='bc_rr_bars'>
        {panel.bars.map((bar) => (
          <div key={bar.label} className='bc_rr_bar_item'>
            <div className='bc_rr_bar_track'>
              <div className='bc_rr_bar_fill' style={{ width: `${clamp((bar.value / maxValue) * 100, 0, 100)}%`, backgroundColor: bar.color }} />
            </div>
            <div className='bc_rr_bar_meta'>
              <span>{bar.label}</span>
              <strong>{bar.displayValue ?? bar.value}</strong>
            </div>
          </div>
        ))}
      </div> : <div className='bc_rr_inline_empty'>Chưa có dữ liệu trong kỳ đã chọn.</div>}
    </div>
  );
};

const DonutPanel = ({ panel }) => {
  const total = panel.segments.reduce((sum, segment) => sum + segment.value, 0);
  let current = 0;
  const gradient = panel.segments
    .map((segment) => {
      const start = current;
      current += (segment.value / total) * 100;
      return `${segment.color} ${start}% ${current}%`;
    })
    .join(', ');

  return (
    <div className='bc_rr_panel_body bc_rr_panel_body_split'>
      <div className='bc_rr_donut' style={{ background: `conic-gradient(${gradient})` }}>
        <div className='bc_rr_donut_center'>
          <strong>{panel.centerValue || '100%'}</strong>
          <span>{panel.centerLabel || 'Tỷ trọng'}</span>
        </div>
      </div>
      <div className='bc_rr_donut_legend'>
        {panel.segments.map((segment) => (
          <div key={segment.label} className='bc_rr_donut_row'>
            <div className='bc_rr_donut_label'>
              <span className='bc_rr_chart_legend_dot' style={{ backgroundColor: segment.color }} />
              <strong>{segment.label}</strong>
            </div>
            <span>{segment.displayValue || `${((segment.value / total) * 100).toLocaleString('vi-VN', { maximumFractionDigits: 1 })}%`}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const TablePanel = ({ panel }) => {
  const [page, setPage] = useState(1);
  const pageSize = panel.width === 'span-12' ? 15 : 6;
  const pages = Math.max(1, Math.ceil(panel.rows.length / pageSize));
  useEffect(() => setPage(1), [panel.rows]);
  return (
  <div className='bc_rr_panel_body'>
    <div className='bc_rr_table_wrap'>
      <table className='bc_rr_table'>
        <thead>
          <tr>
            {panel.columns.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {panel.rows.length ? panel.rows.slice((page - 1) * pageSize, page * pageSize).map((row, rowIndex) => (
            <tr key={`${row[0]}-${rowIndex}`}>
              {row.map((cell, cellIndex) => (
                <td key={`${cellIndex}-${cell}`}>{cell}</td>
              ))}
            </tr>
          )) : (
            <tr>
              <td className='bc_rr_empty' colSpan={panel.columns.length}>Chưa có dữ liệu trong kỳ đã chọn.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    {pages > 1 ? <div className='bc_rr_toolbar'>
      <button className='bc_rr_action_button is-secondary' disabled={page === 1} onClick={() => setPage(page - 1)}>Trang trước</button>
      <span>Trang {page}/{pages} · {panel.rows.length} dòng</span>
      <button className='bc_rr_action_button is-secondary' disabled={page === pages} onClick={() => setPage(page + 1)}>Trang sau</button>
    </div> : null}
  </div>
);
};

const LeaderboardPanel = ({ panel }) => (
  <div className='bc_rr_panel_body'>
    {panel.items.length ? <div className='bc_rr_rank_list'>
      {panel.items.map((item, index) => (
        <div key={item.label} className='bc_rr_rank_item'>
          <div className='bc_rr_rank_index'>{index + 1}</div>
          <div className='bc_rr_rank_copy'>
            <strong>{item.label}</strong>
            <span>{item.meta}</span>
          </div>
          <div className='bc_rr_rank_value'>{item.value}</div>
        </div>
      ))}
    </div> : <div className='bc_rr_inline_empty'>Chưa có dữ liệu trong kỳ đã chọn.</div>}
  </div>
);

const AlertsPanel = ({ panel }) => (
  <div className='bc_rr_panel_body'>
    <div className='bc_rr_alert_list'>
      {panel.items.map((item) => (
        <article key={item.title} className={`bc_rr_alert_card ${getDeltaToneClass(item.tone)}`}>
          <div className='bc_rr_alert_head'>
            {item.tone === 'positive' ? <FiCheckCircle /> : <FiAlertTriangle />}
            <strong>{item.title}</strong>
          </div>
          <p>{item.text}</p>
        </article>
      ))}
    </div>
  </div>
);

const StackedPanel = ({ panel }) => (
  <div className='bc_rr_panel_body'>
    <div className='bc_rr_stacked_list'>
      {panel.rows.map((row) => (
        <div key={row.label} className='bc_rr_stacked_row'>
          <div className='bc_rr_stacked_label'>{row.label}</div>
          <div className='bc_rr_stacked_track'>
            {row.parts.map((part) => (
              <div
                key={`${row.label}-${part.label}`}
                className='bc_rr_stacked_part'
                style={{ width: `${part.value}%`, backgroundColor: part.color }}
                title={`${part.label}: ${part.value}%`}
              />
            ))}
          </div>
          <div className='bc_rr_stacked_total'>{row.displayTotal || '100%'}</div>
        </div>
      ))}
    </div>
  </div>
);

const FunnelPanel = ({ panel }) => (
  <div className='bc_rr_panel_body'>
    <div className='bc_rr_funnel'>
      {panel.steps.map((step) => (
        <div key={step.label} className='bc_rr_funnel_row'>
          <div className='bc_rr_funnel_fill' style={{ width: `${step.width}%`, backgroundColor: step.color }}>
            <strong>{step.label}</strong>
            <span>{step.value}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const HeatmapPanel = ({ panel }) => {
  const maxValue = Math.max(1, ...panel.rows.flatMap((row) => row.values).filter(Number.isFinite));

  return (
    <div className='bc_rr_panel_body bc_rr_heatmap_scroll'>
      <div className='bc_rr_heatmap'>
        <div className='bc_rr_heatmap_head'>
          <span />
          {panel.columns.map((column) => (
            <strong key={column}>{column}</strong>
          ))}
        </div>
        {panel.rows.map((row) => (
          <div key={row.label} className='bc_rr_heatmap_row'>
            <strong>{row.label}</strong>
            {row.values.map((value, index) => (
              <span
                key={`${row.label}-${index}`}
                className='bc_rr_heatmap_cell'
                style={{
                  backgroundColor: value === null ? '#f2f4f7' : `rgba(237, 50, 55, ${clamp(value / maxValue, 0.04, 1)})`,
                  color: value > maxValue * 0.55 ? '#fff' : '#101828',
                }}
              >
                {value === null ? '—' : `${value.toLocaleString('vi-VN', { maximumFractionDigits: 1 })}%`}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const TimelinePanel = ({ panel }) => (
  <div className='bc_rr_panel_body'>
    <div className='bc_rr_timeline'>
      {panel.items.map((item) => (
        <div key={item.label} className='bc_rr_timeline_item'>
          <span className='bc_rr_timeline_dot' />
          <div className='bc_rr_timeline_copy'>
            <strong>{item.label}</strong>
            <span>{item.meta}</span>
          </div>
          <div className='bc_rr_timeline_value'>{item.value}</div>
        </div>
      ))}
    </div>
  </div>
);

const PANEL_COMPONENTS = {
  line: LinePanel,
  bars: BarsPanel,
  donut: DonutPanel,
  table: TablePanel,
  leaderboard: LeaderboardPanel,
  alerts: AlertsPanel,
  stacked: StackedPanel,
  funnel: FunnelPanel,
  heatmap: HeatmapPanel,
  timeline: TimelinePanel,
};

export const RetailReportPanel = ({ panel }) => {
  const PanelIcon = PANEL_ICON_MAP[panel.type] || FiLayers;
  const Content = PANEL_COMPONENTS[panel.type];

  return (
    <section className={`bc_rr_panel ${panel.width || 'span-6'}`}>
      <div className='bc_rr_panel_head'>
        <div className='bc_rr_panel_head_main'>
          <span className='bc_rr_panel_icon'>
            <PanelIcon />
          </span>
          <div>
            <h3>{panel.title}</h3>
            {panel.subtitle ? <p>{panel.subtitle}</p> : null}
          </div>
        </div>
      </div>
      {panel.emptyMessage ? <div className='bc_rr_panel_body'><div className='bc_rr_inline_empty'>{panel.emptyMessage}</div></div> : <Content panel={panel} />}
    </section>
  );
};

const RetailReportPage = () => {
  const { pathname, search } = useLocation();
  const report = useMemo(() => getRetailReportByPath(pathname), [pathname]);
  const reportQuery = useMemo(() => getSalesReportQuery(search), [search]);
  const [portalScope, setPortalScope] = useState(() => getPortalScope());
  const isStoreScopedReport = ['overview', 'revenue-by-store', 'sales-staff-productivity'].includes(report.id);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(() => isStoreScopedReport && Boolean(portalScope.store_id));
  const [reportError, setReportError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const effectiveReport = useMemo(
    () => buildRetailReportFromApi(report, reportData),
    [report, reportData],
  );

  useEffect(() => {
    const handleScopeChange = (event) => setPortalScope(event?.detail || getPortalScope());
    window.addEventListener(PORTAL_SCOPE_CHANGE_EVENT, handleScopeChange);
    return () => window.removeEventListener(PORTAL_SCOPE_CHANGE_EVENT, handleScopeChange);
  }, []);

  useEffect(() => {
    let isMounted = true;
    setReportData(null);
    setReportError('');

    if (!isStoreScopedReport || !portalScope.store_id) {
      setReportLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const loadReport = async () => {
      setReportLoading(true);
      try {
        const request = report.id === 'overview'
          ? getSalesOverviewReport(reportQuery)
          : report.id === 'revenue-by-store'
            ? getRevenueByStoreReport(reportQuery)
            : getSalesStaffProductivityReport(reportQuery);
        const data = await request;
        if (isMounted) setReportData(data);
      } catch (error) {
        if (isMounted) setReportError(error?.message || 'Không thể tải dữ liệu báo cáo.');
      } finally {
        if (isMounted) setReportLoading(false);
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [isStoreScopedReport, portalScope.business_id, portalScope.company_id, portalScope.store_id, refreshKey, report.id, reportQuery]);

  const scopedFilters = useMemo(() => {
    if (!isStoreScopedReport) return effectiveReport.filters;

    const storeName = portalScope.store_name || 'Chưa chọn cửa hàng';
    const businessName = portalScope.business_name;
    const hasStoreFilter = effectiveReport.filters.some((filter) => filter.label === 'Cửa hàng');
    const filters = effectiveReport.filters.map((filter) => {
      if (filter.label === 'Cửa hàng') return { ...filter, value: storeName };
      if (filter.label === 'Khu vực' && businessName) return { ...filter, value: businessName };
      return filter;
    });

    return hasStoreFilter ? filters : [{ label: 'Cửa hàng', value: storeName }, ...filters];
  }, [effectiveReport.filters, isStoreScopedReport, portalScope.business_name, portalScope.store_name]);

  return (
    <ReportPageLayout reportId={effectiveReport.id}>
      <ReportPageHeader
        title={effectiveReport.name}
        description={effectiveReport.subtitle}
        actions={isStoreScopedReport ? (
          <button
            type='button'
            className='bc_rr_action_button is-secondary'
            onClick={() => setRefreshKey((value) => value + 1)}
            disabled={reportLoading || !portalScope.store_id}>
            <FiRefreshCw aria-hidden='true' /> Làm mới
          </button>
        ) : null}
      />
      <ReportContextBar items={scopedFilters} />

      {isStoreScopedReport ? <SalesReportFilters loading={reportLoading} /> : null}
      {reportData?.meta?.unresolved_event_notice ? <ReportState type='error'>
        {reportData.meta.unresolved_event_notice}
      </ReportState> : null}
      {reportData?.meta?.unresolved_return_notice ? <ReportState type='error'>
        {reportData.meta.unresolved_return_notice}
      </ReportState> : null}
      {reportData?.meta?.unresolved_return_cost_notice ? <ReportState type='error'>
        {reportData.meta.unresolved_return_cost_notice}
      </ReportState> : null}
      {reportData?.meta?.unresolved_sale_cost_notice ? <ReportState type='error'>
        {reportData.meta.unresolved_sale_cost_notice}
      </ReportState> : null}

      {isStoreScopedReport && !portalScope.store_id ? (
        <ReportState>Vui lòng chọn cửa hàng trên thanh điều hướng để xem báo cáo.</ReportState>
      ) : null}
      {reportLoading ? (
        <ReportState type='loading'>Đang tổng hợp dữ liệu theo cửa hàng...</ReportState>
      ) : null}
      {reportError ? <ReportState type='error'>{reportError}</ReportState> : null}

      {!reportLoading && !reportError && (!isStoreScopedReport || portalScope.store_id) ? <>
        <ReportKpiGrid metrics={effectiveReport.metrics} />

        <section className='bc_rr_panel_grid' aria-label='Phân tích chi tiết'>
          {effectiveReport.panels.map((panel) => (
            <RetailReportPanel key={`${effectiveReport.id}-${panel.title}`} panel={panel} />
          ))}
        </section>
      </> : null}
    </ReportPageLayout>
  );
};

export default RetailReportPage;
